// SPDX-License-Identifier: AGPL-3.0-or-later

import {GuildShowcasePopout} from '@app/features/guild/components/popouts/GuildShowcasePopout';
import {ServerTagIcon} from '@app/features/guild/components/ServerTagIcons';
import styles from '@app/features/guild/components/ServerTagBadge.module.css';
import Guilds from '@app/features/guild/state/Guilds';
import {VerifiedBadgeIcon} from '@app/features/ui/components/icons/VerifiedBadgeIcon';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import {GuildFeatures} from '@fluxer/constants/src/GuildConstants';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';

interface ServerTagBadgeProps {
	tag: string;
	badge?: string | null;
	color?: string | null;
	guildName?: string;
	guildId?: string;
	className?: string;
	size?: number;
	interactive?: boolean;
	shimmer?: boolean;
}

export const ServerTagBadge: React.FC<ServerTagBadgeProps> = observer(({
	tag,
	badge,
	color,
	guildName,
	guildId,
	className,
	size = 13,
	interactive = true,
	shimmer,
}) => {
	const hasTag = Boolean(tag && tag.trim().length > 0);
	const hasBadge = Boolean(badge && badge !== 'none');

	if (!hasTag && !hasBadge) return null;

	const guild = guildId ? Guilds.getGuild(guildId) : null;
	const resolvedGuildName = guild?.name || guildName;
	const isVerified = guild?.features ? guild.features.has(GuildFeatures.VERIFIED) : false;

	const isGradient = color?.startsWith('linear-gradient');
	const isShimmer = shimmer ?? (isGradient || (guild?.premiumSubscriptionCount ? guild.premiumSubscriptionCount > 0 : false));

	const tooltip = resolvedGuildName
		? `Server Tag • ${resolvedGuildName}`
		: `Server Tag • ${tag || 'Badge'}`;

	const customPillStyle: React.CSSProperties = {
		borderColor: isGradient ? undefined : (color || 'rgba(255, 255, 255, 0.15)'),
	};

	const badgeContent = (
		<span
			className={clsx(
				styles.serverTagBadge,
				interactive && guildId && styles.interactive,
				isShimmer && styles.shimmer,
				isGradient && styles.gradientBorder,
				className,
			)}
			style={customPillStyle}
			title={tooltip}
			data-flx="guild.server-tag-badge"
		>
			{hasBadge && (
				<span className={styles.badgeIconWrapper}>
					<ServerTagIcon badge={badge!} color={color || '#f47fff'} size={size} />
				</span>
			)}
			{hasTag && (
				<span
					className={clsx(styles.tagText, isGradient && styles.gradientText)}
					style={isGradient ? {backgroundImage: color!} : undefined}
				>
					{tag}
				</span>
			)}
			{isVerified && (
				<span className={styles.verifiedCheckWrapper} title="Verified Community">
					<VerifiedBadgeIcon size={12} className={styles.verifiedCheck} />
				</span>
			)}
		</span>
	);

	if (interactive && guildId) {
		return (
			<Popout
				render={({onClose}) => (
					<GuildShowcasePopout guildId={guildId} onClose={onClose} />
				)}
				position="bottom-start"
				animationType="smooth"
				constrainHeight={false}
				freezePosition
			>
				{badgeContent}
			</Popout>
		);
	}

	return badgeContent;
});

export default ServerTagBadge;
